import MprcLoadable from "components/MprcLoadable";
import React from "react";
import { Navigate, Route, Routes } from "react-router";
import { RootPaths } from "routes/RootRouter";
import { BroadcastMessage } from "views/foundation/broadcastMessage/Home";
import { Home } from "views/foundation/home";
import { ListUnitTypes } from "views/foundation/settings/Home/UnitTypes/ListUnitTypes";
import { UserBatchEdit } from "views/foundation/user/Home/BatchEdit";

const UserHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/user/Home")));
const UnitHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/unit")));
const TopicHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/topic")));
const StickerHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/sticker")));
const TagHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/tag")));
const SettingsHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings")));
const CustomFieldHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/customField/Home")));
const GroupHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/group")));
const SecurityRoleHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/security")));

const EditSystemSettings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings/Home/SystemSettings/EditSystemSettings")));
const EditUserSettings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings/Home/User/EditUserSettings")));
const EditAccessAgreement = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings/Home/Agreement/EditAgreements")));
const EditAuth = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings/Home/Auth/EditAuth")));
const EditSystemImages = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings/Home/SystemImages/EditSystemImages")));
const EditRecommendation = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings/Home/Recommendation/EditRecommendation")));
const EditCarousel = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/carousel/Home/Edit/EditCarousel")));
const EditImportUser = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings/Home/ImportUser")));
const ListAwardRanking = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings/Home/AwardRanking")));
const ExcludedLog = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/auditLog/Home/ExcludedLog")));
const WelcomeSettings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings/Home/Welcome")));
const BannerHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/banner")));
const ReportHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/report/Home")));

const ProcessLogHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/scheduler/Home/ProcessLogHome")));
const ListNotificationTemplate = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/notification/Home/ListNotificationTemplate")));
const ListAccessTimeRules = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings/Home/AccessTime/ListAccessTimeRules")));
const ListCalendars = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings/Home/Calendar/ListCalendars")));


// const Overview = lazy(() => import(/* webpackChunkName: 'foundation' */'./pages/Overview'));
// const Subscription = lazy(() => import(/* webpackChunkName: 'foundation' */'./pages/Subscription'));
// const ListCalendars = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'foundation' */"views/foundation/settings/Home/Calendar/ListCalendars").then(({ ListCalendars }) => ({ default: ListCalendars }))));

class Paths {
  static readonly Home = "Home";
  static readonly Group = "Group";
  static readonly Unit = "Unit";
  static readonly SecurityRole = "SecurityRole";
  static readonly Topic = "Topic";
  static readonly Tag = "Tag";
  static readonly Sticker = "Sticker";
  static readonly Banner = "Banner";

  static readonly User = "User";
  static readonly UserBatchEdit = Paths.User + "/BatchEdit";

  static readonly CustomField = "CustomField";
  static readonly Report = "Report";
  static readonly NotificationTemplate = "NotificationsTemplate";
  static readonly ProcessLog = "ProcessLog";
  static readonly ExcludedLog = "ExcludedLog";
  static readonly BroadcastMessage = "BroadcastMessage";
  static readonly LoadData = "LoadData";

  static readonly Settings = "Settings";
  static readonly Personalization = Paths.Settings + "/Personalization";
  static readonly UserSettings = Paths.Settings + "/User";
  static readonly AuthSettings = Paths.Settings + "/Authentication";
  static readonly AccessTimeSettings = Paths.Settings + "/AccessHours";
  static readonly CalendarSettings = Paths.Settings + "/Calendar";
  static readonly TermsOfUseSettings = Paths.Settings + "/TermOfUse";
  static readonly SystemImages = Paths.Settings + "/SystemImages";
  static readonly SystemCarousel = Paths.Settings + "/SystemCarousel";
  static readonly Recommendations = Paths.Settings + "/Recommendations";
  static readonly EditCarousel = Paths.Settings + "/EditCarousel";
  static readonly ImportUser = Paths.Settings + "/ImportUserSettings";
  static readonly Award = Paths.Settings + "/Award";
  static readonly UnitTypes = Paths.Settings + "/UnitTypes";
}

export class FoundationPaths {
  static readonly Prefix = "/foundation";
  static readonly Home = FoundationPaths.Prefix;
  static readonly GroupList = FoundationPaths.Prefix + "/" + Paths.Group;
  static readonly UnitList = FoundationPaths.Prefix + "/" + Paths.Unit;
  static readonly SecurityRoleList = FoundationPaths.Prefix + "/" + Paths.SecurityRole;
  static readonly TopicList = FoundationPaths.Prefix + "/" + Paths.Topic;
  static readonly TagList = FoundationPaths.Prefix + "/" + Paths.Tag;
  static readonly StickerList = FoundationPaths.Prefix + "/" + Paths.Sticker;
  static readonly BannerList = FoundationPaths.Prefix + "/" + Paths.Banner;
  static readonly UserList = FoundationPaths.Prefix + "/" + Paths.User;
  static readonly UserBatchEdit = FoundationPaths.Prefix + "/" + Paths.UserBatchEdit;
  static readonly CustomField = FoundationPaths.Prefix + "/" + Paths.CustomField;
  static readonly ReportList = FoundationPaths.Prefix + "/" + Paths.Report;
  static readonly NotificationTemplate = FoundationPaths.Prefix + "/" + Paths.NotificationTemplate;
  static readonly ProcessLog = FoundationPaths.Prefix + "/" + Paths.ProcessLog;
  static readonly LoadData = FoundationPaths.Prefix + "/" + Paths.ProcessLog + "/" + Paths.LoadData;
  static readonly ExcludedLog = FoundationPaths.Prefix + "/" + Paths.ExcludedLog;
  static readonly BroadcastMessage = FoundationPaths.Prefix + "/" + Paths.BroadcastMessage;
  static EditCarousel(id: string, line: number, module: string) { return FoundationPaths.Prefix + "/" + Paths.EditCarousel + "/" + module + "/" + id + "/" + line; }

  static readonly Settings = FoundationPaths.Prefix + "/" + Paths.Settings;
  static readonly SettingsPersonalization = FoundationPaths.Prefix + "/" + Paths.Personalization;
  static readonly SettingsUser = FoundationPaths.Prefix + "/" + Paths.UserSettings;
  static readonly SettingsAuth = FoundationPaths.Prefix + "/" + Paths.AuthSettings;
  static readonly SettingsAccessTime = FoundationPaths.Prefix + "/" + Paths.AccessTimeSettings;
  static readonly SettingsCalendar = FoundationPaths.Prefix + "/" + Paths.CalendarSettings;
  static readonly SettingsTermsOfUse = FoundationPaths.Prefix + "/" + Paths.TermsOfUseSettings;
  static readonly SettingsSystemImages = FoundationPaths.Prefix + "/" + Paths.SystemImages;
  static readonly SettingsRecommendations = FoundationPaths.Prefix + "/" + Paths.Recommendations;
  static readonly SettingsCarousel = FoundationPaths.Prefix + "/" + Paths.SystemCarousel;
  static readonly SettingsImportUser = FoundationPaths.Prefix + "/" + Paths.ImportUser;
  static readonly SettingsAward = FoundationPaths.Prefix + "/" + Paths.Award;
  static readonly SettingsUnitTypes = FoundationPaths.Prefix + "/" + Paths.UnitTypes;

  static BuildLoadData(queueId?: string | null) { return FoundationPaths.LoadData + `${!!queueId ? `?ldq=${queueId}` : ''}`; }
}

export const FoundationRouter = () => {
  return (
    <Routes>
      <Route path="" element={<Home />} />
      <Route path={Paths.Home} element={<Home />} />
      <Route path={Paths.Group} element={<GroupHome />} />
      <Route path={Paths.Unit} element={<UnitHome />} />
      <Route path={Paths.SecurityRole} element={<SecurityRoleHome />} />
      <Route path={Paths.Topic} element={<TopicHome />} />
      <Route path={Paths.Tag} element={<TagHome />} />
      <Route path={Paths.Sticker} element={<StickerHome />} />
      <Route path={Paths.Banner} element={<BannerHome />} />
      <Route path={Paths.User}>
        <Route index element={<UserHome />} />
        <Route path="BatchEdit" element={<UserBatchEdit />} />
      </Route>
      <Route path={Paths.CustomField} element={<CustomFieldHome />} />
      <Route path={Paths.NotificationTemplate} element={<ListNotificationTemplate />} />
      <Route path={Paths.ProcessLog} >
        <Route index element={<ProcessLogHome />} />
        <Route path={Paths.LoadData} element={<ProcessLogHome tab="loadData" />} />
      </Route>
      <Route path={Paths.Settings} element={<SettingsHome />} />
      <Route path={Paths.Personalization} element={<EditSystemSettings />} />
      <Route path={Paths.UserSettings} element={<EditUserSettings />} />
      <Route path={Paths.AuthSettings} element={<EditAuth />} />
      <Route path={Paths.TermsOfUseSettings} element={<EditAccessAgreement />} />
      <Route path={Paths.SystemImages} element={<EditSystemImages />} />
      <Route path={Paths.SystemCarousel} element={<WelcomeSettings />} />
      <Route path={Paths.AccessTimeSettings} element={<ListAccessTimeRules />} />
      <Route path={Paths.CalendarSettings} element={<ListCalendars />} />
      <Route path={Paths.Recommendations} element={<EditRecommendation />} />
      <Route path={Paths.Award} element={<ListAwardRanking />} />
      <Route path={Paths.UnitTypes} element={<ListUnitTypes />} />
      <Route path={Paths.ImportUser} element={<EditImportUser />} />
      <Route path={Paths.ExcludedLog} element={<ExcludedLog />} />
      <Route path={Paths.BroadcastMessage} element={<BroadcastMessage />} />
      <Route path={Paths.EditCarousel}>
        <Route path=":module/:id/:line" element={<EditCarousel />} />
      </Route>

      <Route path={Paths.Report} element={<ReportHome />} />

      <Route path="*" element={<Navigate to={RootPaths.PageNotFound} />} />
    </Routes>
  );
};
